import type { Client, ClassificationGroup, ClassificationCategory, TenderClassificationItem } from '@/pages/Tenders/types';

export const opportunityStatuses = ['decision_pending', 'in_study', 'submitted', 'awarded', 'discarded'] as const;
export type OpportunityStatus = typeof opportunityStatuses[number];
export type CalendarView = 'day' | 'week' | 'month' | 'year';
export interface Opportunity {
    id: number; code: string; title: string; workflow_status: OpportunityStatus; procurement_type: 'public' | 'private' | null;
    client_id: number; client?: Client; budget: string | null; currency_code: string;
    submission_at: string | null; submission_date: string | null; submission_time: string | null; submission_timezone: string;
    city: string | null; province_id: number | null; country_id: number | null; province?: { id: number; name: string };
    record_version: number; ever_advanced: boolean; deleted_at: string | null; created_by: number; responsible_id: number | null;
    can_delete?: boolean; allowed_transitions?: OpportunityStatus[]; submitted_version_id: number | null;
    cancellation_reason: string | null; discarded_from: OpportunityStatus | null; discard_category: string | null;
    description?: string | null; external_reference?: string | null; valuation_criteria?: string | null;
    deadline_period?: number | null; deadline_unit_id?: number | null; project_type_id?: number | null; designer?: string | null;
    address?: string | null; street_number?: string | null; postal_code?: string | null; submission_mode?: string | null;
    observations?: string | null; annuities?: string | null; is_ute?: boolean; is_closed_price?: boolean;
    general_expenses_percentage?: string | null; industrial_profit_percentage?: string | null; vat_percentage?: string | null;
    provisional_guarantee_percentage?: string | null; definitive_guarantee_percentage?: string | null;
    subcontracting_percentage?: string | null; contractor_expenses?: string | null; contractor_expenses_description?: string | null;
    payment_method_id?: number | null; classifications?: TenderClassificationItem[];
}
export interface OpportunityDocument { id: number; name: string; purpose: string; size: number; created_at: string; }
export interface OfferVersion {
    id: number; number: number; name: string; amount: string | null; bc3_percentage: string; document_id: number | null;
    submitted_at: string | null; snapshot: Record<string, unknown>; bc3_data: Bc3Data | null;
}
export interface Bc3Data {
    root_code: string; base_amount: string; amount: string;
    concepts: Record<string, { code: string; summary: string; unit: string; price: string }>;
    decompositions: Record<string, { code: string; factor: string; quantity: string }[]>;
    measurements: { reference: string; total: string }[];
}
export interface OpportunityEvent {
    id: number; action: string; actor_id: number | null; created_at: string; reason: string | null; document_ids: number[];
    changes: Record<string, { before: unknown; after: unknown }>;
}
export interface OpportunityDetail extends Opportunity {
    documents: OpportunityDocument[]; versions: OfferVersion[]; events: OpportunityEvent[];
    actors: { id: number; name: string }[]; legacy_study_ids: number[];
    legacy_logs: { id: number; action: string; created_at: string; user_id: number | null; notes: string | null }[];
}
export interface PlanningReminder {
    id: number; title: string; starts_at: string; timezone: string; notes: string | null;
    is_completed: boolean; record_version: number; tender_id: number | null;
}
export interface CalendarData { opportunities: Opportunity[]; reminders: PlanningReminder[]; upcoming: Opportunity[]; undated_count: number; counts?: Partial<Record<OpportunityStatus, number>>; }
export interface CatalogItem { id: number; name: string; }
export interface OpportunityCatalogs {
    countries: CatalogItem[]; provinces: CatalogItem[]; project_types: CatalogItem[]; deadline_units: CatalogItem[];
    payment_methods: CatalogItem[]; responsibles: CatalogItem[]; classification_groups: ClassificationGroup[]; classification_categories: ClassificationCategory[];
}
export interface OpportunityFilters {
    search: string; status: string; client_id: string; responsible_id: string; city: string; designer: string;
    classification: string; procurement_type: string; from: string; to: string; upcoming: boolean; overdue: boolean; deleted: boolean;
}
export const emptyFilters: OpportunityFilters = { search: '', status: 'all', client_id: '', responsible_id: '', city: '', designer: '', classification: '', procurement_type: '', from: '', to: '', upcoming: false, overdue: false, deleted: false };
